import { GetStaticProps, NextPage } from 'next';
import { Container, Row, Col } from 'reactstrap';
import Image from 'next/image';
import { RichText } from 'prismic-reactjs';
import { Client } from '../utils';
import { ImageComponent } from '../components/Media';
import { Slideshow } from '../components/Slider';
import { Layout, SeoMeta } from '../components/Layout';
import { Section } from '../components/Sections';

interface Props {
  content?: any;
}

const Views: NextPage<Props> = ({ content }) => {
  return (
    <Layout>
      <SeoMeta
        title={RichText.asText(content.seo__title)}
        desc={RichText.asText(content.seo__description)}
        socialImage={content.seo__image}
      />

      {content.slideshow__image.url && (
        <Slideshow
          src={content.slideshow__image.url || ''}
          alt={content.slideshow__image.alt || ''}
          width={4830}
          height={1667}
        />
      )}

      {((content.headline && RichText.asText(content.headline)) ||
        (content.intro__text &&
          RichText.asText(content.intro__text) !== '')) && (
        <Section hasTop>
          <Container fluid>
            <Row>
              <Col md={{ size: 6 }} lg={{ size: 6 }}>
                <h1 className="font--xxl font--primary">
                  {RichText.asText(content.headline) || 'Views'}
                </h1>
              </Col>
              <Col md={{ size: 6, offset: 0 }} lg={{ size: 4, offset: 2 }}>
                {RichText.asText(content.intro__text) !== '' && (
                  <>{RichText.render(content.intro__text)}</>
                )}
              </Col>
            </Row>
          </Container>
        </Section>
      )}

      {content.intro__image && content.intro__image.url && (
        <Section>
          <Container fluid>
            <Row>
              <Col md={{ size: 12, offset: 0 }} lg={{ size: 8, offset: 4 }}>
                <ImageComponent
                  src={content.intro__image.url}
                  alt={content.intro__image.alt}
                  alignment="horizontal"
                  offset="full"
                />
              </Col>
            </Row>
          </Container>
        </Section>
      )}

      {RichText.asText(content.neighborhood__text) !== '' && (
        <Section>
          <Container fluid>
            <Row>
              <Col md={{ size: 6, offset: 6 }} lg={{ size: 4, offset: 8 }}>
                <>{RichText.render(content.neighborhood__text)}</>
              </Col>
            </Row>
          </Container>
        </Section>
      )}

      {((content.park__image && content.park__image.url) ||
        (content.park__text && RichText.asText(content.park__text) !== '') ||
        (content.river__image && content.river__image.url)) && (
        <Section hasBottomMobile={false}>
          <Container fluid>
            <Row>
              <Col md={{ size: 6, offset: 0 }}>
                {content.park__image && content.park__image.url && (
                  <div className="m-t-image--sm">
                    <ImageComponent
                      src={content.park__image.url}
                      alt={content.park__image.alt}
                      alignment="horizontal"
                      offset="left-full"
                    />
                  </div>
                )}
                {RichText.asText(content.park__text) !== '' && (
                  <Col md={12} lg={8}>
                    <div className="m-t">
                      <>{RichText.render(content.park__text)}</>
                    </div>
                  </Col>
                )}
              </Col>
              {content.river__image && content.river__image.url && (
                <Col
                  md={{ size: 6, offset: 0 }}
                  lg={{ size: 4, offset: 2 }}
                  className="m-t"
                >
                  <ImageComponent
                    src={content.river__image.url}
                    alt={content.river__image.alt}
                    alignment="vertical"
                    offset="right-full"
                  />
                </Col>
              )}
            </Row>
          </Container>
        </Section>
      )}
      {((content.skyline__image && content.skyline__image.url) ||
        (content.skyline__text &&
          RichText.asText(content.skyline__text) !== '')) && (
        <Section>
          <Container fluid>
            <Row>
              {content.skyline__image && content.skyline__image.url && (
                <Col md={{ size: 6, offset: 0 }} lg={{ size: 8, offset: 0 }}>
                  <div className="m-t-image--sm">
                    <div className="container--border-radius container--offset-left--full">
                      <Image
                        src={content.skyline__image.url}
                        alt={content.skyline__image.alt}
                        layout="responsive"
                        blurDataURL={content.skyline__image.url}
                        placeholder="blur"
                        width={content.skyline__image.dimensions.width}
                        height={content.skyline__image.dimensions.height}
                      />
                    </div>
                  </div>
                </Col>
              )}
              {RichText.asText(content.skyline__text) !== '' && (
                <Col
                  className="flex flex--align-center"
                  md={{ size: 6, offset: 0 }}
                  lg={{ size: 4, offset: 0 }}
                >
                  <div className="m-t--sm">
                    <>{RichText.render(content.skyline__text)}</>
                  </div>
                </Col>
              )}
            </Row>
          </Container>
        </Section>
      )}
    </Layout>
  );
};

export const getStaticProps: GetStaticProps = async () => {
  // https://prismic.io/docs/technologies/query-by-type-rest-api
  const res = await Client.query('[at(document.type, "views")]');
  const data = res.results.map((content: { data: any }) => {
    return content.data;
  });
  const content = data[0];

  return {
    props: {
      content,
    },
  };
};

export default Views;
